import { useSyncExternalStore } from "react";
import { getLanguage, isConsoleLanguage, setLanguage, subscribeLanguage, type ConsoleLanguage } from "./index";

/**
 * The language picked in the header, for this browser tab only. There is no
 * endpoint yet to save a staff setting (UPDATE_STAFF_SETTING_STAFF_ACTION is
 * planned), so the pick lives in sessionStorage and the account's
 * `consoleLanguage` is unchanged. Storage can be missing or throw (private
 * mode, blocked site data): then the pick lasts until the page reloads.
 */

const KEY = "walliyo.console.language";

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function sessionStore(): Store | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/** The language picked for this session, or null when none was (or storage is unreadable). */
export function readSessionLanguage(store: Store | null = sessionStore()): ConsoleLanguage | null {
  try {
    const value = store?.getItem(KEY);
    return isConsoleLanguage(value) ? value : null;
  } catch {
    return null;
  }
}

export function clearSessionLanguage(store: Store | null = sessionStore()): void {
  try {
    store?.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
}

/** The header switcher: shows the console in `next` now, and remembers it for this session. */
export function chooseLanguage(next: ConsoleLanguage, store: Store | null = sessionStore()): void {
  try {
    store?.setItem(KEY, next);
  } catch {
    // Not remembered, but still applied until the page reloads.
  }
  setLanguage(next);
}

/** Which language to show once the staff member is known: this session's pick, else their setting. */
export function effectiveLanguage(
  accountLanguage: ConsoleLanguage,
  store: Store | null = sessionStore(),
): ConsoleLanguage {
  return readSessionLanguage(store) ?? accountLanguage;
}

/** The current language; re-renders the component when it changes. */
export function useLanguage(): ConsoleLanguage {
  return useSyncExternalStore(subscribeLanguage, getLanguage, getLanguage);
}
